import { ChangeDetectionStrategy, Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, finalize, takeUntil } from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import { AiModelService } from '@6grain/masofa-monolith-api-client/common/api/aiModel.service';
import { AiModelDto } from '@6grain/masofa-monolith-api-client/common/model/aiModelDto';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { MsfSelect } from '@components/select/select';
import { Loading } from '@components/ui/loading/loading';

@Component({
  selector: 'app-onnx-process-model',
  standalone: true,
  imports: [RouterModule, TranslateModule, DialogModule, PrimaryButton, SecondaryButton, MsfSelect, Loading],
  templateUrl: './onnx-process-model.html',
  styleUrl: './onnx-process-model.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnnxProcessModelComponent implements OnDestroy {
  private readonly api = inject(AiModelService);
  private readonly destroy$ = new Subject<void>();
  readonly visible = signal(false);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly loadError = signal(false);
  readonly saveError = signal(false);
  readonly saved = signal(false);
  readonly models = signal<AiModelDto[]>([]);
  readonly selectedId = signal<string | null>(null);
  readonly activeModels = computed(() => this.models().filter(model =>
    model.serviceKey === 'OnnxCropClassification' && model.status === 'Active'
    && model.lifecycleState !== 'Archived',
  ));
  readonly currentModel = computed(() => this.activeModels().find(model => model.isDefault) ?? null);
  readonly currentLabel = computed(() => this.currentModel() ? this.label(this.currentModel()!) : '');
  readonly options = computed(() => this.activeModels()
    .filter(model => !!model.id && !!model.onnxFileName?.trim())
    .map(model => ({ id: model.id!, label: this.label(model) })));
  readonly canSave = computed(() => !this.loading() && !this.saving() && !this.loadError()
    && this.selectedId() !== this.currentModel()?.id
    && this.options().some(option => option.id === this.selectedId()));

  open(): void {
    this.visible.set(true);
    this.saved.set(false);
    this.saveError.set(false);
    this.reload();
  }

  close(): void {
    if (!this.saving()) this.visible.set(false);
  }

  select(id: string | null): void {
    if (this.loading() || this.saving()) return;
    this.selectedId.set(id);
    this.saved.set(false);
    this.saveError.set(false);
  }

  reload(): void {
    if (this.loading() || this.saving()) return;
    this.loading.set(true);
    this.loadError.set(false);
    this.models.set([]);
    this.selectedId.set(null);
    this.api.aiInferenceAiModelGetListPost({ serviceKey: 'OnnxCropClassification', activeOnly: true })
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading.set(false)))
      .subscribe({
        next: models => {
          this.models.set(models ?? []);
          const current = this.currentModel();
          this.selectedId.set(this.options().some(option => option.id === current?.id) ? current!.id! : null);
        },
        error: () => this.loadError.set(true),
      });
  }

  save(): void {
    if (!this.canSave()) return;
    const modelId = this.selectedId()!;
    this.saving.set(true);
    this.saveError.set(false);
    this.saved.set(false);
    this.api.aiInferenceAiModelSetDefaultPost({ modelId })
      .pipe(takeUntil(this.destroy$), finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.saved.set(true);
          this.saving.set(false);
          this.reload();
        },
        error: () => this.saveError.set(true),
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private label(model: AiModelDto): string {
    return [model.name || model.code || model.id, model.version, model.onnxFileName].filter(Boolean).join(' · ');
  }
}
